/*
 Copyright (C) 2018 Yaroslav Kikot
 This project is licensed under the terms of the MIT license.
 https://github.com/Zicrael/ngx-tree-dnd
 */

import { Component, Input, AfterViewInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';

import { NgxTreeService } from '../ngx-tree-dnd.service';
import { TreeModel, TreeConfig, TreeItemOptions } from '../models/tree-view.model';

@Component({
  selector: 'lib-ngx-tree-children',
  template: `<div class='tree-child' id={{element.id}} libDragElement [draggableValue]='element.options.draggable' [item]='element' [ngClass]="{disabled : element.options.disabled}">
  <div *ngIf="element && element.options" class='d-flex'>
    <div *ngIf='config' [ngClass]="{hidden : element.options.hidden}">
      <div class='tree-title d-inline-flex pos-relative' [ngClass]="{destenationTop : element.options.destenationTop, destenationBottom: element.options.destenationBottom}" *ngIf="!element.options.edit;else onEdit">
        <div *ngIf="!config.setItemsAsLinks; else link" [ngClass]="{addOpacity : element.options.currentlyDragging}" libDropElement
          [item]='element' class='draggable-item'>
          {{element.name}}
        </div>
        <ng-template #link>
          <div [ngClass]="{addOpacity : element.options.currentlyDragging}" libDropElement
          [item]='element' class='draggable-item'>
            <a [href]="element.options.href" class='tree-link'>{{element.name}}</a>
          </div>
        </ng-template>
        <div class='d-flex buttons-bar' *ngIf="config.showActionButtons && element.options.showActionButtons && !element.options.disabled">
          <div class='d-flex'>
            <button class="tree-btn add-btn" *ngIf="config.showAddButtons && element.options.showAddButtons" (click)="submitAdd(null, element)">
              <fa-icon icon="plus" [style.font-size.px]='config.setIconSize'></fa-icon>
            </button>
          </div>
          <div class='d-flex'>
            <button class="tree-btn edit-btn" *ngIf="config.showRenameButtons" (click)="enableRenameMode(element)">
              <fa-icon icon="edit" [style.font-size.px]='config.setIconSize'></fa-icon>
            </button>
          </div>
          <div class='d-flex'>
            <button class="tree-btn delete-btn" *ngIf="config.showDeleteButtons && element.options.showDeleteButton" (click)="onSubmitDelete( element )">
              <fa-icon icon="times" [style.font-size.px]='config.setIconSize'></fa-icon>
            </button>
          </div>
        </div>
        <div class='child-drop-place' [attr.data-id]='element.id'  libDropElement *ngIf='element.options.showDropChildZone && !element.options.disabled'>
          <fa-icon icon="arrow-down" [style.font-size.px]='config.setIconSize'></fa-icon>
        </div>
        <div class='show-hide-switch' *ngIf="config.enableExpandButtons && element.options.showExpandButton && element.childrens.length > 0 && !element.options.disabled">
          <div *ngIf="element.options.hideChildrens; else visible">
            <button class='tree-btn show-btn' (click)='element.options.hideChildrens = false'>
              <fa-icon icon="plus" [style.font-size.px]='config.setIconSize'></fa-icon>
            </button>
          </div>
          <ng-template #visible>
            <button class='tree-btn hide-btn' (click)='element.options.hideChildrens = true'>
              <fa-icon icon="minus" [style.font-size.px]='config.setIconSize'></fa-icon>
            </button>
          </ng-template>
        </div>
      </div>
      <ng-template #onEdit>
        <div class='tree-title d-inline-flex'>
          <form [formGroup]="renameForm" class='d-flex' (submit)='submitRename(element)'>
            <input type="text" class='input-rename' formControlName="name" libAutoFocus="true" [style.font-size.px]='config.setFontSize'>
          </form>
          <div class='d-flex'>
            <button class='tree-btn submit-btn' (click)='submitRename(element)'>
              <fa-icon icon="check" [style.font-size.px]='config.setIconSize'></fa-icon>
            </button>
            <button class='tree-btn delete-btn' (click)='onSubmitDelete(element)'>
              <fa-icon icon="times" [style.font-size.px]='config.setIconSize'></fa-icon>
            </button>
            <div class='error-edit-wrap' *ngIf="showError">
              {{config.validationText}}
            </div>
            <div class='error-edit-wrap' *ngIf="!itemOptions.isValid">
              {{config.validationText}}
            </div>
          </div>
        </div>
      </ng-template>
      <div class="tree-content" *ngIf="element.childrens && !element.options.hideChildrens">
        <lib-ngx-tree-children [setItem]="child" *ngFor='let child of element.childrens'></lib-ngx-tree-children>
      </div>
    </div>
  </div>
</div>
`
})
export class NgxTreeChildrenComponent implements AfterViewInit {
  showError: boolean;
  config: TreeConfig;
  element: TreeModel;
  dragable: boolean;
  itemOptions: TreeItemOptions;
  childrensArray: TreeModel[];
  renameForm;

  // get item from parent component
  @Input()
  set setItem(data: TreeModel) {
    this.element = data;
    this.itemOptions = {
      href: '#',
      hidden: false,
      hideChildrens: false,
      position: this.treeService.getItemPosition(this.element),
      draggable: true,
      edit: false,
      showActionButtons: true,
      currentlyDragging: false,
      showAddButtons: true,
      destenationTop: false,
      destenationBottom: false,
      disabled: false,
      showExpandButton: true,
      showDeleteButton: true,
      isValid: true
    };
    if (this.element.options) {
      this.setOptions(this.element.options);
      this.element.options = this.itemOptions;
    } else {
      this.element.options = this.itemOptions;
    }

    // enable subscribers
    this.enableSubscribers();
    // create form
    this.createForm();
  }

  constructor(private treeService: NgxTreeService, private fb: FormBuilder) {}

  // enable subscribe to config
  enableSubscribers() {
    this.treeService.config.subscribe(
      (config) => {
        if ( config !== null ) {
          this.config = config;
        } else {
          this.config = this.treeService.defaulConfig;
        }
        if (this.element.options.draggable) {
          this.element.options.draggable = this.config.enableDragging;
        }
      }
    );
  }

  // set options to item
  setOptions(options) {
    for (const key of Object.keys(options)) {
      this.setValue(key, options);
    }
  }

  // set value to options keys
  setValue(item, options) {
    this.itemOptions[item] = options[item];
  }

  // create edit form
  createForm() {
    this.renameForm = this.fb.group({
      name: [this.element.name || '' , [
        Validators.required,
        Validators.minLength( this.config.minCharacterLength )
      ]],
    });
  }

  /*
    Event: onStartRenameItem;
    Enable rename mode in element
    Call onStartRenameItem() from tree service.
  */
  enableRenameMode(element) {
    element.options.edit = true;
    this.treeService.startRenameItem(element);
  }

  /*
    Event: onadditem;
    Generate id by new Date() by 'full year + day + time'.
    Call addNewItem() from tree service.
  */
  submitAdd(name, item ) {
      const d = `${new Date().getFullYear()}${new Date().getDay()}${new Date().getTime()}`;
      const elemId =  parseInt(d, null);
      this.treeService.addNewItem(elemId, name, item);
      this.element.options.hideChildrens = false;
  }

  /*
    Event: onFinishRenameItem;
    Check is form valid.
    Call addNewItem() from tree service.
  */
 submitRename(item) {
  if (this.renameForm.valid) {
    this.showError = false;
    this.treeService.validateItem(this.renameForm.value.name, item.id);
    // this.treeService.finishRenameItem(this.renameForm.value.name, item.id);
    // this.element.options.edit = false;
  } else {
    this.showError = true;
  }
}

  /*
    Events:
    onStartDeleteItem,
    onFinishDeleteItem,    
    onCancelDeleteItem.
    Check is item edit, then if name empty delete item.
    Call deleteItem() from tree service.
  */
  onSubmitDelete(item) {
    if (!this.element.options.edit) {
      this.treeService.deleteItem(item.id);
    } else {
      if ( item.name === null ) {
        this.treeService.deleteItem(item.id);
      } else {
        this.element.options.edit = false;
      }
    }
  }

  // after view init
  ngAfterViewInit() {}
}
